import Table from "src/features/fields/components/Table";
import { SettledSettlementColumns } from "../columns";
import { Fragment, useState, useEffect, useRef } from "react";
import Button from "src/features/fields/components/Button";
import { Transition } from "@headlessui/react";
import { SortingState } from "@tanstack/react-table";
import SettlementFilter from "src/features/fields/container/SettlementFilter";
import { useOutsideClick } from "src/hooks/useOutsideClick";
import { useSelector } from "react-redux";
import { RootState } from "src/context/store";
import FilterIcon from "src/assets/filter.svg";
import axios from "axios";
import { useToast } from "src/hooks/useToast";
import { usePagination } from "src/features/fields/hooks/usePagination";
import { Download } from "lucide-react";

function SettledSettlement({ viewMode = 'list' }: any) {

  const { settlementFiltersData, filters } = useSelector((state: RootState) => state.settlement as any);
  const { SETTLED } = filters;

  const [data, setData] = useState([]);
  const [pageCount, setPageCount] = useState(1);
  const [isFiltersShown, setIsFiltersShown] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false)
  const [sorting, setSorting] = useState<SortingState>();

  const ref = useOutsideClick(() => setIsFiltersShown(false));
  const { pagination, onPaginationChange, pageIndex, pageSize } = usePagination();
  const { toast } = useToast();
  const requestControllerRef = useRef<AbortController | null>(null);

  const getFilterValue = (filterArray: any[]) => {
    return Array.isArray(filterArray) && filterArray.length ? filterArray[0] : null;
  };

  const buildRequestBody = (pgIndex: number, pgSize: number, selectedView: string) => {
    const view = selectedView === "list";
    const baseBody = {
      PageNo: view ? `${pgIndex}` : "1",
      PageSize: view ? `${pgSize}` : "1000",
    };

    let filters = SETTLED;

    if (!filters) return baseBody;

    const company = getFilterValue(filters.company);
    const stockist = getFilterValue(filters.stockist);
    const division = getFilterValue(filters.division);
    const product = getFilterValue(filters.product);
    const cn = getFilterValue(filters.cn);
    const shortage = getFilterValue(filters.shortage);
    const dateRange = filters.dateRange;

    if (!company || !stockist) {
      return null;
    }

    const requestBody = {
      ...baseBody,
      CompanyCode: (company?.code)?.toString(),
      CompanyDivision_Code: (division?.company_divisioncode)?.toString() || "",
      ProductCode: product?.item_code || "",
      UserUcode: stockist?.uid || "",
      From_Date: dateRange?.from || "",
      To_Date: dateRange?.to || "",
      CN_Flg: cn?.value === "with" ? true : false,
      Shortage_Flag: shortage?.value === "with" ? true : false,
    };

    return requestBody;
  };

  const abortPreviousRequest = () => {
    if (requestControllerRef.current) {
      requestControllerRef.current.abort();
    }
  };

  const fetchSettlementData = async (
    pageIndex: number,
    pageSize: number, selectedView: string) => {
    abortPreviousRequest();
    const controller = new AbortController();
    requestControllerRef.current = controller;

    const api = import.meta.env.VITE_V1_STOCKIST_API;
    const apiEndpoint = `${api}/Settlement/GetSettlementSettled`

    try {

      setIsLoading(true);
      const requestBody = buildRequestBody(pageIndex, pageSize, selectedView);

      if (!requestBody) {
        toast({ variant: "default", description: "Company and Stockist filters are mandatory." });
        setIsLoading(false);
        return;
      }

      const response = await axios.post(
        apiEndpoint,
        requestBody, {
        signal: controller.signal,
      }
      );
      setData(response.data.result)
      setPageCount(response?.data?.pagination?.total_number);
    } catch (error: any) {
      if (error.name === "CanceledError" || error.code === "ERR_CANCELED") {
        console.error("Duplicate request cancelled:", error);
        return;
      }
      console.error("Error fetching order data:", error);
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    fetchSettlementData(pageIndex, pageSize, viewMode);
    return () => {
      abortPreviousRequest();
    };
  }, [sorting, pageIndex, pageSize, viewMode, SETTLED]);

  const fetchSettlementDataForDownload = async () => {
    const api = import.meta.env.VITE_V1_STOCKIST_API;
    const apiEndpoint = `${api}/Settlement/GetSettlementSettled`;

    const requestBody = buildRequestBody(1, 10000, "list");

    if (!requestBody) {
      toast({
        variant: "default",
        description: "Company and Stockist filters are mandatory.",
      });
      return [];
    }

    setIsDownloading(true)
    try {
      const response = await axios.post(apiEndpoint, requestBody);
      return response?.data?.result || [];
    } catch (error) {
      console.error("Download API failed:", error);
      return [];
    }
    finally {
      setIsDownloading(false)
    }
  };

  const convertToCSV = (arr: any[]) => {
    if (!arr || !arr.length) return "";

    const keys = Object.keys(arr[0]);

    const csvRows = [
      keys.join(","),
      ...arr.map((obj) =>
        keys
          .map((k) => {
            const val =
              obj[k] === null || obj[k] === undefined
                ? ""
                : String(obj[k]);
            return `"${val.replace(/"/g, '""')}"`;
          })
          .join(",")
      ),
    ];

    return csvRows.join("\r\n");
  };

  const handleDownload = async () => {
    try {
      const results = await fetchSettlementDataForDownload();

      if (!results.length) {
        toast({
          variant: "default",
          description: "No data available to download.",
        });
        return;
      }

      const csv = convertToCSV(results);

      const blob = new Blob([csv], {
        type: "text/csv;charset=utf-8;",
      });

      const url = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        "settled_settlement.csv"
      );

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Download failed:", err);
    }
  };

  return <Fragment>
    <Transition
      as={Fragment}
      show={isFiltersShown}
      enter="transition transition ease-in-out duration-200 transform"
      enterFrom="translate-x-full"
      enterTo="translate-x-0"
      leave="transition  ease-in-out duration-200 transform"
      leaveFrom="translate-x-0"
      leaveTo="translate-x-full"
    >
      <div className="fixed right-0 inset-0 top-0 z-[500]">
        <SettlementFilter
          ref={ref}
          type="SETTLED"
          companyDetails={settlementFiltersData?.company || []}
          onCloseClick={() => setIsFiltersShown(false)}
        />
      </div>
    </Transition>

    <div className="flex flex-col">
      <div className="flex justify-end float-right gap-2 mb-5">
        <Button
          onClick={handleDownload}
          className="gap-1 px-4 mt-2 ml-2 border relative"
          isLoading={isDownloading}
          loaderSize="small"
        >
          <Download size={18} />
        </Button>
        <Button
          className="gap-1 px-4 mt-2 ml-2 border relative"
          onClick={() => setIsFiltersShown(true)}
        >
          Filters
          <img src={FilterIcon} className="w-5 h-5" />
          {SETTLED?.filterCount > 0 && (
            <span className="absolute top-0 right-1 flex items-center justify-center w-5 h-5 text-xs text-white bg-red-500 rounded-full">
              {SETTLED.filterCount}
            </span>
          )}
        </Button>
        <Button onClick={() => fetchSettlementData(pageIndex, pageSize, viewMode)} loaderSize="small" className="h-auto py-2 mt-2 border">
          Retrieve
        </Button>
      </div>

      <Table
        headClass="bg-[#dee2ff] text-[#586AF5]"
        columns={SettledSettlementColumns}
        data={data ?? []}
        isLoading={isLoading}
        isPaginating={viewMode !== "tree"}
        manualPagination={true}
        pageCount={pageCount}
        pagination={pagination}
        onPaginationChange={onPaginationChange}
        sorting={sorting}
        onSortingChange={setSorting}
        manualSorting={true}
        groupingColumns={
          viewMode === "tree"
            ? ["Company_Name", "Division_Name", "OrderType_Name", "Order_Number", "Stk_Invoice_Date", "Stk_Invoice_Number", "Item_Name", "Expected_Date_of_Release", "Amount", "Quantity", "Discount_Type", "Discount_Value"]
            : undefined
        }
      />
    </div>
  </Fragment>
}

export default SettledSettlement;